import { Component, OnInit } from '@angular/core';
import { StudyEditorContext } from '../../study-editor.context';
import { ObservationVariable, ValueReference } from '../../../shared/model/observation-variable.model';
import { VariableTypeEnum } from '../../../shared/ontology/variable-type.enum';
import { VariableDetails } from '../../../shared/ontology/model/variable-details';
import { StudyVariableSelectModalComponent } from '../../shared/modals/variable-select-modal/study-variable-select-modal.component';
import { NgbDateAdapter, NgbDateParserFormatter, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { VariableService } from '../../../shared/ontology/service/variable.service';
import { AlertService } from '../../../shared/alert/alert.service';
import { Variable } from '../../../shared/ontology/model/variable';
import { ADD_TREATMENT_FACTORS_VARIABLES_PERMISSIONS, GENERATE_EXPERIMENTAL_DESIGN_PERMISSIONS } from '../../../shared/auth/permissions';
import { Principal } from '../../../shared';
import { VariableSearchRequest } from '../../../shared/ontology/model/variable-search-request.model';
import { HELP_MANAGE_STUDIES_TREATMENT_FACTORS } from '../../../app.constants';
import { TreatmentVariable } from '../../../shared/study/model/treatment-variable.model';
import { DataTypeIdEnum } from '../../../shared/ontology/data-type.enum';
import { CustomStringDateAdapter } from '../../shared/adapters/custom-string-date-adapter';
import { CustomDateParserFormatter } from '../../shared/adapters/custom-date-parser-formatter';
import { DatasetService } from '../../../shared/dataset/service/dataset.service';
import { Cell } from '../../../shared/dataset/model/observation-unit-data.model';

@Component({
    selector: 'jhi-treatment-factors',
    templateUrl: './treatment-factors.component.html',
    styleUrls: ['./treatment-factors.component.scss'],
    providers: [
        { provide: NgbDateAdapter, useClass: CustomStringDateAdapter },
        { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter }
    ]
})
export class TreatmentFactorsComponent implements OnInit {

    protected readonly DataTypeIdEnum = DataTypeIdEnum;
    protected readonly VariableTypeEnum = VariableTypeEnum;
    protected readonly HIDE_FROM_VARIABLES_TABLE: number[];
    protected readonly ADD_TREATMENT_FACTORS_VARIABLES_PERMISSIONS = ADD_TREATMENT_FACTORS_VARIABLES_PERMISSIONS;
    protected readonly HELP_MANAGE_STUDIES_TREATMENT_FACTORS = HELP_MANAGE_STUDIES_TREATMENT_FACTORS;
    protected readonly GENERATE_EXPERIMENTAL_DESIGN_PERMISSIONS = GENERATE_EXPERIMENTAL_DESIGN_PERMISSIONS;

    pairItemsMap: { [key:number]: ObservationVariable[] } = {};

    private subscription: Subscription;

    disableTreatmentFactor = false;

    constructor(protected studyEditorContext: StudyEditorContext,
                private modalService: NgbModal,
                private variableService: VariableService,
                private alertService: AlertService,
                private datasetService: DatasetService,
                public principal: Principal) { }

    ngOnInit(): void {
        this.disableTreatmentFactor = this.studyEditorContext.hasGeneratedDesign() || !this.principal.hasAnyAuthorityDirect(ADD_TREATMENT_FACTORS_VARIABLES_PERMISSIONS);
        this.studyEditorContext.studyDetails.treatmentFactors.forEach(tf => {
            tf.categoricalValues = tf.valueVariable?.possibleValues?.map((possibleValue) => {
                return { id: String(possibleValue.id), name: possibleValue.name };
            });
            if (tf.categoricalValues?.length > 0) {
                tf.categoricalValuesMap = new Map(
                    tf.categoricalValues?.map(item => [item.id, item.name])
                );
            }
            if (tf.values == null) {
                tf.values = [];
            }
        });
    }

    onAddVariableClick(): void {
        const modal = this.modalService.open(StudyVariableSelectModalComponent as Component, {
            modalDialogClass: 'modal-lg',
            backdrop: 'static'
        });
        const modalRef = modal.componentInstance;
        modalRef.variableTypeIds = [VariableTypeEnum.TREATMENT_FACTOR.valueOf()];
        modalRef.existingVariableIdsInStudy = this.studyEditorContext.studyDetails.treatmentFactors.map(tf => tf.levelVariable.termId);

        this.subscription = modalRef.variableSelect.subscribe(async (variable: VariableDetails) => {
            if (variable) {
                const pairs = await this.variableService.getVariablePairs(parseInt(variable.id, 10)).toPromise();
                if (pairs && pairs.length > 0) {
                    const pairVariables = await this.getVariables(pairs.map(variable => parseInt(variable.id, 10)));
                    this.pairItemsMap[variable.id] = pairVariables.map(pair => this.convertVariableToObvervationVariable(pair));
                    const levelVariable = this.convertVariableDetailsToObvervationVariable(variable);
                    const valueVariable = this.pairItemsMap[variable.id][0];
                    let treatmentVar = {
                        levelVariable,
                        valueVariable,
                        values: [],
                        categoricalValues: valueVariable.possibleValues.map((possibleValue) => {
                            return {
                                id: String(possibleValue.id),
                                name: possibleValue.name
                            };
                        })
                    };
                    this.studyEditorContext.studyDetails.treatmentFactors.push(treatmentVar);
                    this.saveTreatmentFactor(treatmentVar);
                } else {
                    this.alertService.error('study-editor.treatment-factor.validation.variable-added-has-no-pair');
                }
            }
        });

        // Clean up when modal is closed
        modal.closed.subscribe(() => {
            if (this.subscription) {
                this.subscription.unsubscribe();
            }
        });
    }

    async removeVariable(variable: ObservationVariable) {
        const index = this.studyEditorContext.studyDetails.treatmentFactors.findIndex(
            tf => tf.levelVariable.termId === variable.termId
        );
        if (index > -1) {
            this.datasetService.deleteTreatmentFactor(this.studyEditorContext.studyDetails.id, variable.termId).toPromise().then(() => {
                this.studyEditorContext.studyDetails.treatmentFactors.splice(index, 1);
            });
        }
    }

    createArray(levels: number) {
        const array = [];
        for (let i = 0; i < levels; i++) {
            array.push(i);
        }
        return array;
    }

    convertVariableDetailsToObvervationVariable(variableDetails: VariableDetails): ObservationVariable {
        return {
            termId: parseInt(variableDetails.id, 10),
            name: variableDetails.name,
            alias: variableDetails.alias,
            property: variableDetails.property.name,
            scale: variableDetails.scale.name,
            method: variableDetails.method.name
        };
    }

    convertVariableToObvervationVariable(variable: Variable): ObservationVariable {

        let dataTypeId = DataTypeIdEnum.CHARACTER;
        if (variable.scale.dataType as string === 'CATEGORICAL_VARIABLE') {
            dataTypeId = DataTypeIdEnum.CATEGORICAL;
        } else if (variable.scale.dataType as string === 'NUMERIC_VARIABLE') {
            dataTypeId = DataTypeIdEnum.NUMERIC;
        } else if (variable.scale.dataType as string === 'CHARACTER_VARIABLE') {
            dataTypeId = DataTypeIdEnum.CHARACTER;
        } else if (variable.scale.dataType as string === 'DATE_TIME_VARIABLE') {
            dataTypeId = DataTypeIdEnum.DATE;
        }
        return {
            termId: parseInt(variable.id, 10),
            name: variable.name,
            alias: variable.alias,
            property: variable.property?.name || '',
            scale: variable.scale?.name || '',
            method: '', // Variable interface doesn't have method property
            dataTypeId: dataTypeId,
            possibleValues: variable.scale?.categories?.map((category) => {
                return {
                    id: parseInt(category.id, 10),
                    name: category.name,
                    description: category.description
                };
            })
        };
    }

    async getVariables(variableIds: number[] = []) {
        const request: VariableSearchRequest = <VariableSearchRequest>({});
        request.variableIds = variableIds;
        const variablesResponse = await this.variableService.searchVariables(request).toPromise();
        return variablesResponse.body;
    }

    getSelectOptions(possibleValues: ValueReference[]) {
        if (possibleValues) {
            return possibleValues.map(possibleValue => {
                return { id: possibleValue.id.toString(), name: possibleValue.name };
            });
        }
        return [];
    }

    onTreatmentVariableChange($event: any, treatmentFactor: TreatmentVariable) {
        treatmentFactor.categoricalValues = [];
        if (treatmentFactor.valueVariable && treatmentFactor.valueVariable.possibleValues) {
            treatmentFactor.categoricalValues = treatmentFactor.valueVariable.possibleValues.map((possibleValue) => {
                return {
                    id: String(possibleValue.id),
                    name: possibleValue.name
                };
            });
        }
    }

    hasGeneratedDesign() {
        return this.studyEditorContext.hasGeneratedDesign();
    }

    getCategoricalValueName(treatmentFactor:TreatmentVariable, idx: number) {
        if (treatmentFactor.categoricalValues?.length > 0) {
            return treatmentFactor.categoricalValuesMap.get(treatmentFactor.values[idx]);
        }

        return treatmentFactor.values[idx];
    }

    async saveTreatmentFactor(treatmentVar: TreatmentVariable) {
        await this.datasetService.addTreatmentFactor(this.studyEditorContext.studyDetails.id, treatmentVar).toPromise();
    }
    async onValueChanged(treatmentVar: TreatmentVariable) {
        await this.datasetService.editTreatmentFactor(this.studyEditorContext.studyDetails.id, treatmentVar).toPromise();
    }
}
